import React, { type ReactElement } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { createProjectUrl } from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/common/create-url';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	Header as CommonNav4Header,
	type Breadcrumb,
} from '@atlassian/jsm-enterprise-reports-common';
import messages from './messages';

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	title,
	children,
	baseUrl,
	projectKey,
	projectName,
}: {
	title: string;
	children: ReactElement;
	baseUrl: string;
	projectKey: string;
	projectName: string;
}) => {
	const { formatMessage } = useIntl();
	const breadcrumbs: Breadcrumb[] = [
		{ href: '/jira/projects', text: formatMessage(messages.projectsBreadcrumb) },
		{ href: createProjectUrl(projectKey || ''), text: projectName || '' },
		{
			href: `${baseUrl}/jira/servicedesk/projects/${projectKey}/reports`,
			text: formatMessage(messages.reportsBreadcrumb),
		},
	];
	return getWillShowNav4() ? (
		<Box xcss={commonNav4HeaderWrapper}>
			<Box xcss={nav4HeaderWrapper}>
				<CommonNav4Header title={title} breadcrumbs={breadcrumbs} actions={children} />
			</Box>
		</Box>
	) : (
		<TitleContainer>
			<Heading size="large" as="h1">
				<StyledTitle data-test-id="servicedesk-reports-common.ui.header.title">{title}</StyledTitle>
			</Heading>
			{children}
		</TitleContainer>
	);
};

const commonNav4HeaderWrapper = xcss({
	marginLeft: 'space.negative.100',
	marginRight: 'space.negative.100',
});
const nav4HeaderWrapper = xcss({
	marginLeft: 'space.negative.400',
	marginRight: 'space.negative.400',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTitle = styled.span({
	lineHeight: token('space.400', '32px'),
	marginTop: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div({
	display: 'flex',
	marginTop: token('space.negative.200', '-16px'),
});
